import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService, Account, AccountSummary, PlaidIntegration } from '../services/api';
import { usePrivacy } from '../contexts/PrivacyContext';
import { Button } from '../components/ui/button';
import { 
  Wallet, 
  Trash2, 
  Eye, 
  RefreshCw, 
  Landmark,
  Building2,
  CreditCard,
  PiggyBank,
  ArrowUpRight,
  ArrowDownRight,
  Plus
} from 'lucide-react';
import { PlaidLinkButton } from '../components/PlaidLinkButton';

// Account type icons
const getAccountIcon = (type: string) => {
  switch (type) {
    case 'checking':
      return Building2;
    case 'savings':
      return PiggyBank;
    case 'credit':
      return CreditCard;
    default:
      return Wallet;
  }
};

export const AccountsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isPrivacyMode } = usePrivacy();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [summary, setSummary] = useState<AccountSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [integrations, setIntegrations] = useState<PlaidIntegration[]>([]);
  const [syncingIntegrationId, setSyncingIntegrationId] = useState<string | null>(null);
  const [syncingAll, setSyncingAll] = useState(false);

  const fetchAccounts = async (signal?: AbortSignal, forceIntegrations: boolean = true) => {
    try {
      setLoading(true);
      setError(null);
      
      const [accountsResponse, integrationsResponse] = await Promise.all([
        apiService.getAccounts(signal),
        apiService.getPlaidIntegrationsOnce({ signal, force: forceIntegrations }),
      ]);
      
      // Only update state if not aborted
      if (!signal?.aborted) {
        setAccounts(accountsResponse.accounts);
        setSummary(accountsResponse.summary);
        setIntegrations(integrationsResponse.integrations);
      }
    } catch (err) {
      // Ignore abort errors
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      if (!signal?.aborted) {
        setError(err instanceof Error ? err.message : 'Failed to load accounts');
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    fetchAccounts(abortController.signal);
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteAccount = async (accountId: string, accountName: string) => {
    const warningMessage = `Are you sure you want to delete "${accountName}"?\n\nThis will:\n- Delete all associated transactions\n- Remove the account from your dashboard\n\nNote: This will not close your actual bank account. If you reconnect this bank later, the account may be imported again.`;
    
    if (!window.confirm(warningMessage)) {
      return;
    }

    try {
      setError(null);
      await apiService.deleteAccount(accountId);
      await fetchAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete account');
    }
  };

  const handleViewTransactions = (accountId: string) => {
    navigate(`/transactions?account_id=${accountId}`);
  };

  const handlePlaidSuccess = async () => {
    const accountsBefore = accounts.length;
    await fetchAccounts();
    const accountsAfter = accounts.length;

    // If this was the first account connection, automatically sync transactions
    if (accountsBefore === 0 && accountsAfter > 0) {
      try {
        // Sync all newly connected accounts
        for (const account of accounts) {
          if (account.plaid_integration_id) {
            await apiService.syncPlaidTransactions(account.plaid_integration_id);
          }
        }
        // Show success message
        alert(`Successfully connected ${accountsAfter} account(s) and synced transactions!`);
      } catch (err) {
        console.error('Failed to auto-sync transactions:', err);
        // Still show that accounts were connected even if sync failed
        alert(`Successfully connected ${accountsAfter} account(s)! Transaction sync will happen in the background.`);
      }
    }
  };

  const handleSyncAccount = async (account: Account) => {
    if (!account.plaid_integration_id) return;
    
    try {
      setSyncingIntegrationId(account.plaid_integration_id);
      setError(null);
      
      const response = await apiService.syncPlaidTransactions(account.plaid_integration_id);

      alert(response.message || `Sync started for ${account.name}. Transactions will update in the background.`);
      
      await fetchAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync account');
    } finally {
      setSyncingIntegrationId(null);
    }
  };

  const handleSyncAllAccounts = async () => {
    const activeIntegrations = integrations.filter(i => i.status === 'active');
    
    if (activeIntegrations.length === 0) {
      setError('No bank accounts to sync');
      return;
    }
    
    try {
      setSyncingAll(true);
      setError(null);
      
      for (const integration of activeIntegrations) {
        try {
          await apiService.syncPlaidTransactions(integration.id);
        } catch (err) {
          console.error(`Failed to sync integration ${integration.id}:`, err);
        }
      }
      
      alert(`Sync started for ${syncableAccountsCount} account(s). Transactions will update in the background.`);
      
      await fetchAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync accounts');
    } finally {
      setSyncingAll(false);
    }
  };

  const formatAmount = (amount: string) => {
    if (isPrivacyMode) return '$••••';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parseFloat(amount));
  };

  const formatCompact = (amount: string | number) => {
    if (isPrivacyMode) return '$••••';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  // Use summary from API response
  const netWorth = summary ? parseFloat(summary.net_worth) : 0;
  const totalAssets = summary ? parseFloat(summary.total_assets) : 0;
  const totalLiabilities = summary ? parseFloat(summary.total_liabilities) : 0;
  const syncableAccountsCount = accounts.filter(a => a.source === 'plaid' && a.plaid_integration_id).length;
  const integrationMap = useMemo(() => {
    const map = new Map<string, PlaidIntegration>();
    integrations.forEach((integration) => {
      map.set(integration.id, integration);
    });
    return map;
  }, [integrations]);

  const renderIntegrationStatus = (status: PlaidIntegration['status']) => {
    switch (status) {
      case 'active':
        return (
          <span
            className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
            style={{ 
              backgroundColor: 'color-mix(in srgb, var(--theme-income) 12%, transparent)',
              color: 'var(--theme-income)'
            }}
          >
            Active
          </span>
        );
      case 'error':
        return (
          <span
            className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
            style={{ 
              backgroundColor: 'color-mix(in srgb, var(--theme-expense) 15%, transparent)',
              color: 'var(--theme-expense)'
            }}
          >
            Error
          </span>
        );
      case 'disconnected':
        return (
          <span
            className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
            style={{ 
              backgroundColor: 'color-mix(in srgb, var(--theme-text-secondary) 12%, transparent)',
              color: 'var(--theme-text-secondary)'
            }}
          >
            Disconnected
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ backgroundColor: 'var(--theme-background)' }}>
      {/* Header */}
      <div className="px-4 pt-6 pb-4 md:px-8 md:pt-8 md:pb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div className="fade-in-up">
            <h1 
              className="text-2xl md:text-3xl font-bold tracking-tight"
              style={{ color: 'var(--theme-text)' }}
            >
              Accounts
            </h1>
            <p 
              className="text-sm mt-1"
              style={{ color: 'var(--theme-text-secondary)' }}
            >
              Manage your connected accounts
            </p>
          </div>
          
          <div className="flex gap-2 fade-in-up" style={{ animationDelay: '100ms' }}>
            {syncableAccountsCount > 0 && (
              <Button
                onClick={handleSyncAllAccounts}
                disabled={syncingAll}
                variant="outline"
                size="sm"
                className="gap-2 rounded-xl transition-all"
                style={{ 
                  backgroundColor: 'var(--theme-surface)',
                  borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 20%, transparent)',
                  color: 'var(--theme-text)'
                }}
              >
                <RefreshCw className={`h-4 w-4 ${syncingAll ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{syncingAll ? 'Syncing...' : 'Sync All'}</span>
              </Button>
            )}
            <PlaidLinkButton
              onSuccess={handlePlaidSuccess}
              onError={(error) => setError(error)}
              variant="default"
              size="sm"
              className="gap-2 rounded-xl bg-[var(--theme-primary)]"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Add Account</span>
              <span className="sm:hidden">Add</span>
            </PlaidLinkButton>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-4 pb-6 md:px-8 md:pb-8">
        {/* Error Alert */}
        {error && (
          <div 
            className="apple-card mb-4 !p-4 fade-in-up"
            style={{ 
              backgroundColor: 'color-mix(in srgb, var(--theme-expense) 10%, var(--theme-surface))',
              borderColor: 'color-mix(in srgb, var(--theme-expense) 30%, transparent)'
            }}
          >
            <p className="text-sm font-medium" style={{ color: 'var(--theme-expense)' }}>
              {error}
            </p>
          </div>
        )}

        {loading ? (
          /* Loading State */
          <div className="flex flex-col items-center justify-center py-16">
            <div 
              className="w-10 h-10 border-3 rounded-full animate-spin mb-4"
              style={{ 
                borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 20%, transparent)',
                borderTopColor: 'var(--theme-primary)'
              }}
            />
            <p className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>
              Loading accounts...
            </p>
          </div>
        ) : accounts.length === 0 ? (
          /* Empty State */
          <div className="apple-card p-8 md:p-12 text-center fade-in-up">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: 'color-mix(in srgb, var(--theme-primary) 12%, transparent)' }}
            >
              <Landmark className="h-8 w-8" style={{ color: 'var(--theme-primary)' }} />
            </div>
            <h3 
              className="text-lg font-semibold mb-2"
              style={{ color: 'var(--theme-text)' }}
            >
              No accounts connected
            </h3>
            <p 
              className="text-sm mb-6 max-w-sm mx-auto"
              style={{ color: 'var(--theme-text-secondary)' }}
            >
              Connect your bank account to automatically sync your transactions and balances
            </p>
            <PlaidLinkButton
              onSuccess={handlePlaidSuccess}
              onError={(error) => setError(error)}
              variant="default"
              size="default"
              className="rounded-xl bg-[var(--theme-primary)]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Connect Bank Account
            </PlaidLinkButton>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-3 md:gap-4 fade-in-up" style={{ animationDelay: '150ms' }}>
              {/* Net Worth Card */}
              <div className="apple-card group">
                <div className="flex items-center justify-between mb-2">
                  <div 
                    className="p-2 rounded-xl"
                    style={{ 
                      backgroundColor: netWorth >= 0 
                        ? 'color-mix(in srgb, var(--theme-income) 12%, transparent)' 
                        : 'color-mix(in srgb, var(--theme-expense) 12%, transparent)' 
                    }}
                  >
                    <Wallet 
                      className="h-4 w-4" 
                      style={{ 
                        color: netWorth >= 0 ? 'var(--theme-income)' : 'var(--theme-expense)' 
                      }} 
                    />
                  </div>
                </div>
                <p 
                  className="text-[10px] md:text-xs uppercase tracking-wider font-medium mb-1"
                  style={{ color: 'var(--theme-text-secondary)' }}
                >
                  Net Worth
                </p>
                <p 
                  className="text-lg md:text-2xl font-bold tracking-tight"
                  style={{ color: netWorth >= 0 ? 'var(--theme-income)' : 'var(--theme-expense)' }}
                >
                  {formatCompact(netWorth)}
                </p>
              </div>

              {/* Assets Card */}
              <div className="apple-card group">
                <div className="flex items-center justify-between mb-2">
                  <div 
                    className="p-2 rounded-xl"
                    style={{ backgroundColor: 'color-mix(in srgb, var(--theme-income) 12%, transparent)' }}
                  >
                    <ArrowUpRight className="h-4 w-4" style={{ color: 'var(--theme-income)' }} />
                  </div>
                </div>
                <p 
                  className="text-[10px] md:text-xs uppercase tracking-wider font-medium mb-1"
                  style={{ color: 'var(--theme-text-secondary)' }}
                >
                  Assets
                </p>
                <p 
                  className="text-lg md:text-2xl font-bold tracking-tight"
                  style={{ color: 'var(--theme-income)' }}
                >
                  {formatCompact(totalAssets)}
                </p>
              </div>

              {/* Liabilities Card */}
              <div className="apple-card group">
                <div className="flex items-center justify-between mb-2">
                  <div 
                    className="p-2 rounded-xl"
                    style={{ backgroundColor: 'color-mix(in srgb, var(--theme-expense) 12%, transparent)' }}
                  >
                    <ArrowDownRight className="h-4 w-4" style={{ color: 'var(--theme-expense)' }} />
                  </div>
                </div>
                <p 
                  className="text-[10px] md:text-xs uppercase tracking-wider font-medium mb-1"
                  style={{ color: 'var(--theme-text-secondary)' }}
                >
                  Liabilities
                </p>
                <p 
                  className="text-lg md:text-2xl font-bold tracking-tight"
                  style={{ color: 'var(--theme-expense)' }}
                >
                  {formatCompact(Math.abs(totalLiabilities))}
                </p>
              </div>
            </div>

            {/* Accounts List */}
            <div>
              <h2 
                className="text-base font-semibold mb-3 fade-in-up"
                style={{ color: 'var(--theme-text)', animationDelay: '200ms' }}
              >
                Your Accounts ({accounts.length})
              </h2>
              
              <div className="apple-card !p-0 overflow-hidden fade-in-up" style={{ animationDelay: '250ms' }}>
                {accounts.map((account, index) => {
                  const AccountIcon = getAccountIcon(account.account_type);
                  const isLast = index === accounts.length - 1;
                  const isSyncing = syncingIntegrationId === account.plaid_integration_id;
                  const integration = account.plaid_integration_id 
                    ? integrationMap.get(account.plaid_integration_id) 
                    : null;
                  
                  return (
                    <div
                      key={account.id}
                      className="group"
                      style={{ 
                        borderBottom: !isLast 
                          ? '1px solid color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)' 
                          : 'none'
                      }}
                    >
                      <div className="flex items-center gap-4 p-4 md:p-5">
                        {/* Account Icon */}
                        <div 
                          className="p-3 rounded-2xl flex-shrink-0 transition-transform duration-300 group-hover:scale-105"
                          style={{ backgroundColor: 'color-mix(in srgb, var(--theme-primary) 10%, transparent)' }}
                        >
                          <AccountIcon 
                            className="h-5 w-5 md:h-6 md:w-6" 
                            style={{ color: 'var(--theme-primary)' }} 
                          />
                        </div>
                        
                        {/* Account Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 
                              className="font-semibold text-sm md:text-base truncate"
                              style={{ color: 'var(--theme-text)' }}
                            >
                              {account.name}
                            </h3>
                            {integration && renderIntegrationStatus(integration.status)}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span 
                              className="text-xs capitalize"
                              style={{ color: 'var(--theme-text-secondary)' }}
                            >
                              {account.account_type}
                            </span>
                            {account.masked_number && (
                              <>
                                <span style={{ color: 'var(--theme-text-secondary)', opacity: 0.5 }}>•</span>
                                <span 
                                  className="text-xs"
                                  style={{ color: 'var(--theme-text-secondary)' }}
                                >
                                  •••• {account.masked_number}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Balance */}
                        <div className="text-right flex-shrink-0">
                          <p 
                            className="font-bold text-base md:text-lg"
                            style={{ color: 'var(--theme-text)' }}
                          >
                            {formatAmount(account.balance)}
                          </p>
                        </div>
                        
                        {/* Desktop Actions - Icon buttons */}
                        <div className="hidden md:flex items-center gap-1 flex-shrink-0">
                          {account.source === 'plaid' && account.plaid_integration_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSyncAccount(account)}
                              disabled={isSyncing}
                              className="h-9 w-9 p-0 rounded-xl hover:bg-[color-mix(in_srgb,var(--theme-primary)_10%,transparent)]"
                              style={{ color: 'var(--theme-text-secondary)' }}
                              title="Sync transactions"
                            >
                              <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewTransactions(account.id)}
                            className="h-9 w-9 p-0 rounded-xl hover:bg-[color-mix(in_srgb,var(--theme-primary)_10%,transparent)]"
                            style={{ color: 'var(--theme-text-secondary)' }}
                            title="View transactions"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAccount(account.id, account.name)}
                            className="h-9 w-9 p-0 rounded-xl hover:bg-[color-mix(in_srgb,var(--theme-expense)_10%,transparent)]"
                            style={{ color: 'var(--theme-text-secondary)' }}
                            title="Delete account"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Mobile Actions - Full width button row */}
                      <div className="flex gap-2 px-4 pb-4 md:hidden">
                        {account.source === 'plaid' && account.plaid_integration_id && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSyncAccount(account)}
                            disabled={isSyncing}
                            className="flex-1 h-10 rounded-xl text-xs font-medium gap-1.5"
                            style={{ 
                              backgroundColor: 'color-mix(in srgb, var(--theme-primary) 5%, var(--theme-surface))',
                              borderColor: 'color-mix(in srgb, var(--theme-primary) 20%, transparent)',
                              color: 'var(--theme-primary)'
                            }}
                          >
                            <RefreshCw className={`h-3.5 w-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
                            {isSyncing ? 'Syncing...' : 'Sync'}
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewTransactions(account.id)}
                          className="flex-1 h-10 rounded-xl text-xs font-medium gap-1.5"
                          style={{ 
                            backgroundColor: 'var(--theme-surface)',
                            borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 15%, transparent)',
                            color: 'var(--theme-text-secondary)'
                          }}
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Transactions
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteAccount(account.id, account.name)}
                          className="h-10 w-10 p-0 rounded-xl flex-shrink-0"
                          style={{ 
                            backgroundColor: 'color-mix(in srgb, var(--theme-expense) 5%, var(--theme-surface))',
                            borderColor: 'color-mix(in srgb, var(--theme-expense) 20%, transparent)',
                            color: 'var(--theme-expense)'
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};
